/**
 * SpreadRenderer.js
 *
 * Combines two page images into a complete open magazine spread with:
 *   - Per-page curve distortion (via PageCurve)
 *   - A spine shadow gradient between the pages
 *   - A soft outer drop shadow
 *   - Optional perspective warp (via PerspectiveWarp)
 *
 * The rendered result is returned as an offscreen HTMLCanvasElement, ready to
 * be consumed by Fabric.js as a `fabric.Image` source.
 *
 * @module SpreadRenderer
 */

import { renderPageCurve } from './PageCurve.js';
import { renderPerspective, createPerspectiveQuad } from './PerspectiveWarp.js';

/* ------------------------------------------------------------------ */
/*  Default configuration                                              */
/* ------------------------------------------------------------------ */

/** @type {SpreadOptions} */
const DEFAULTS = {
  width: 1200,
  height: 800,
  pageCurve: true,
  curveIntensity: 0.15,
  lightingEnabled: true,
  spineShadow: true,
  spineShadowWidth: 30,
  dropShadow: true,
  dropShadowBlur: 15,
  dropShadowOffsetX: 0,
  dropShadowOffsetY: 8,
  dropShadowColor: 'rgba(0, 0, 0, 0.45)',
  perspective: null, // { tiltX, rotateY, skewX, skewY }
  backgroundColor: null, // if set, fills behind the spread
  pageGap: 2, // pixels between the two pages at the spine
  pagePadding: 0, // inner padding shrunk into each page area
};

/**
 * @typedef {object} SpreadOptions
 * @property {number}  [width=1200]
 * @property {number}  [height=800]
 * @property {boolean} [pageCurve=true]
 * @property {number}  [curveIntensity=0.15]
 * @property {boolean} [lightingEnabled=true]
 * @property {boolean} [spineShadow=true]
 * @property {number}  [spineShadowWidth=30]
 * @property {boolean} [dropShadow=true]
 * @property {number}  [dropShadowBlur=15]
 * @property {number}  [dropShadowOffsetX=0]
 * @property {number}  [dropShadowOffsetY=8]
 * @property {string}  [dropShadowColor='rgba(0,0,0,0.45)']
 * @property {object|null} [perspective=null]
 * @property {string|null} [backgroundColor=null]
 * @property {number}  [pageGap=2]
 */

/* ------------------------------------------------------------------ */
/*  SpreadRenderer class                                               */
/* ------------------------------------------------------------------ */

export class SpreadRenderer {
  /**
   * Create a SpreadRenderer with default options that can be overridden
   * per-render call.
   *
   * @param {Partial<SpreadOptions>} [defaults]
   */
  constructor(defaults = {}) {
    /** @type {SpreadOptions} */
    this.defaults = { ...DEFAULTS, ...defaults };
  }

  /**
   * Render a complete open magazine spread.
   *
   * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} leftPageImg
   * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} rightPageImg
   * @param {Partial<SpreadOptions>} [options]
   * @returns {HTMLCanvasElement} – the rendered spread
   */
  render(leftPageImg, rightPageImg, options = {}) {
    const opts = { ...this.defaults, ...options };
    const {
      width,
      height,
      pageCurve,
      curveIntensity,
      lightingEnabled,
      spineShadow,
      spineShadowWidth,
      dropShadow,
      dropShadowBlur,
      dropShadowOffsetX,
      dropShadowOffsetY,
      dropShadowColor,
      perspective,
      backgroundColor,
      pageGap,
    } = opts;

    // ------ Phase 1: Render both pages flat (with curve) onto a working canvas ------
    const spreadCanvas = document.createElement('canvas');
    const margin = dropShadow ? dropShadowBlur * 2 + Math.abs(dropShadowOffsetX) + Math.abs(dropShadowOffsetY) : 0;
    // Extra vertical headroom for page curve displacement
    const curveHeadroom = pageCurve ? Math.ceil(height * 0.045 * curveIntensity) : 0;

    const innerW = width;
    const innerH = height;
    spreadCanvas.width = innerW;
    spreadCanvas.height = innerH + curveHeadroom;

    const sCtx = spreadCanvas.getContext('2d');
    sCtx.imageSmoothingEnabled = true;
    sCtx.imageSmoothingQuality = 'high';

    const halfW = Math.floor(innerW / 2) - Math.floor(pageGap / 2);

    // -- Left page --
    if (leftPageImg) {
      if (pageCurve) {
        renderPageCurve(sCtx, leftPageImg, 0, curveHeadroom, halfW, innerH, {
          curveIntensity,
          isLeftPage: true,
          lightingEnabled,
        });
      } else {
        sCtx.drawImage(leftPageImg, 0, curveHeadroom, halfW, innerH);
      }
    }

    // -- Right page --
    if (rightPageImg) {
      const rightX = Math.ceil(innerW / 2) + Math.ceil(pageGap / 2);
      if (pageCurve) {
        renderPageCurve(sCtx, rightPageImg, rightX, curveHeadroom, halfW, innerH, {
          curveIntensity,
          isLeftPage: false,
          lightingEnabled,
        });
      } else {
        sCtx.drawImage(rightPageImg, rightX, curveHeadroom, halfW, innerH);
      }
    }

    // -- Spine shadow --
    if (spineShadow) {
      this._drawSpineShadow(sCtx, innerW, innerH, curveHeadroom, spineShadowWidth, curveIntensity);
    }

    // ------ Phase 2: Optionally apply perspective warp ------
    let resultSource = spreadCanvas;

    if (perspective) {
      const pCanvas = document.createElement('canvas');
      const pw = innerW + margin * 2;
      const ph = innerH + curveHeadroom + margin * 2;
      pCanvas.width = pw;
      pCanvas.height = ph;
      const pCtx = pCanvas.getContext('2d');
      pCtx.imageSmoothingEnabled = true;
      pCtx.imageSmoothingQuality = 'high';

      const quad = createPerspectiveQuad(margin, margin, innerW, innerH + curveHeadroom, {
        rotateY: perspective.rotateY || 0,
        tiltX: perspective.tiltX || 0,
        skewX: perspective.skewX || 0,
        skewY: perspective.skewY || 0,
      });

      renderPerspective(pCtx, spreadCanvas, {
        x: 0, y: 0,
        w: spreadCanvas.width,
        h: spreadCanvas.height,
      }, quad);

      resultSource = pCanvas;
    }

    // ------ Phase 3: Composite onto final canvas with drop shadow ------
    const finalCanvas = document.createElement('canvas');
    if (perspective) {
      finalCanvas.width = resultSource.width;
      finalCanvas.height = resultSource.height;
    } else {
      finalCanvas.width = innerW + margin * 2;
      finalCanvas.height = innerH + curveHeadroom + margin * 2;
    }
    const fCtx = finalCanvas.getContext('2d');
    fCtx.imageSmoothingEnabled = true;
    fCtx.imageSmoothingQuality = 'high';

    // Background fill
    if (backgroundColor) {
      fCtx.fillStyle = backgroundColor;
      fCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    }

    // Drop shadow
    if (dropShadow) {
      // 1. Soft, wide ambient shadow for lifted page edges
      fCtx.save();
      fCtx.shadowColor = dropShadowColor;
      fCtx.shadowBlur = dropShadowBlur * 1.5;
      fCtx.shadowOffsetX = dropShadowOffsetX;
      fCtx.shadowOffsetY = dropShadowOffsetY;
      
      if (perspective) {
        fCtx.drawImage(resultSource, 0, 0);
      } else {
        fCtx.fillStyle = '#fff';
        // Inset slightly so it represents the page curve lifting
        fCtx.fillRect(margin + 10, margin + 10, innerW - 20, innerH + curveHeadroom - 20);
      }
      fCtx.restore();

      // 2. Sharp, dark contact shadow at the spine (if no perspective is applied, or approximated)
      if (!perspective) {
        fCtx.save();
        fCtx.shadowColor = 'rgba(0,0,0,0.8)';
        fCtx.shadowBlur = dropShadowBlur * 0.4;
        fCtx.shadowOffsetX = 0;
        fCtx.shadowOffsetY = dropShadowOffsetY * 0.5;
        fCtx.fillStyle = '#fff';
        // Narrow strip down the spine
        fCtx.fillRect(margin + innerW / 2 - 2, margin, 4, innerH + curveHeadroom);
        fCtx.restore();
      }

      // Draw spread imagery on top
      if (!perspective) {
        fCtx.drawImage(resultSource, margin, margin);
      }
    } else {
      // No shadow — centre the spread
      const ox = perspective ? 0 : margin;
      const oy = perspective ? 0 : margin;
      fCtx.drawImage(resultSource, ox, oy);
    }
    
    // ------ Phase 4: Global Glossy Environmental Lighting ------
    if (lightingEnabled) {
      fCtx.save();
      // Only apply gloss over the non-transparent parts of the spread
      fCtx.globalCompositeOperation = 'source-atop';
      
      const glossGrad = fCtx.createLinearGradient(0, 0, finalCanvas.width, finalCanvas.height);
      // Simulates a window/light source reflection across the glossy spread
      glossGrad.addColorStop(0, 'rgba(255, 255, 255, 0.15)');
      glossGrad.addColorStop(0.3, 'rgba(255, 255, 255, 0.05)');
      glossGrad.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
      glossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.15)'); // Ambient occlusion opposite the light
      
      fCtx.fillStyle = glossGrad;
      fCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      fCtx.restore();
    }

    return finalCanvas;
  }

  /* ---------------------------------------------------------------- */
  /*  Private helpers                                                   */
  /* ---------------------------------------------------------------- */

  /**
   * Draw the spine shadow — a vertical gradient band at the centre of the
   * spread that creates the illusion of depth between the two pages.
   *
   * @private
   */
  _drawSpineShadow(ctx, spreadWidth, spreadHeight, curveHeadroom, shadowWidth, intensity) {
    const cx = spreadWidth / 2;
    // Wider ambient spread for the shadow
    const halfW = shadowWidth * (1 + intensity * 2);
    const y0 = curveHeadroom;

    // Left-side gradient (fading left from spine)
    ctx.save();
    const gradL = ctx.createLinearGradient(cx - halfW, 0, cx, 0);
    gradL.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradL.addColorStop(0.5, `rgba(0, 0, 0, ${(0.05 * intensity * 4).toFixed(3)})`); // Ambient
    gradL.addColorStop(0.85, `rgba(0, 0, 0, ${(0.15 * intensity * 4).toFixed(3)})`); // Crease start
    gradL.addColorStop(0.98, `rgba(0, 0, 0, ${(0.45 * intensity * 4).toFixed(3)})`); // Deep crease
    gradL.addColorStop(1, `rgba(0, 0, 0, ${(0.8 * intensity * 4).toFixed(3)})`);     // Pitch black center
    ctx.fillStyle = gradL;
    ctx.fillRect(cx - halfW, y0, halfW, spreadHeight);

    // Right-side gradient (fading right from spine)
    const gradR = ctx.createLinearGradient(cx, 0, cx + halfW, 0);
    gradR.addColorStop(0, `rgba(0, 0, 0, ${(0.8 * intensity * 4).toFixed(3)})`);
    gradR.addColorStop(0.02, `rgba(0, 0, 0, ${(0.45 * intensity * 4).toFixed(3)})`);
    gradR.addColorStop(0.15, `rgba(0, 0, 0, ${(0.15 * intensity * 4).toFixed(3)})`);
    gradR.addColorStop(0.5, `rgba(0, 0, 0, ${(0.05 * intensity * 4).toFixed(3)})`);
    gradR.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradR;
    ctx.fillRect(cx, y0, halfW, spreadHeight);
    ctx.restore();

    // Top/Bottom Ambient Occlusion in the crevice (where paper pinches together)
    ctx.save();
    const vertGrad = ctx.createLinearGradient(0, y0, 0, y0 + spreadHeight);
    vertGrad.addColorStop(0, `rgba(0,0,0,${0.6 * intensity * 4})`);
    vertGrad.addColorStop(0.08, 'rgba(0,0,0,0)');
    vertGrad.addColorStop(0.92, 'rgba(0,0,0,0)');
    vertGrad.addColorStop(1, `rgba(0,0,0,${0.6 * intensity * 4})`);
    ctx.fillStyle = vertGrad;
    ctx.fillRect(cx - shadowWidth, y0, shadowWidth * 2, spreadHeight);
    ctx.restore();
  }
}
